import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import {
  normalizeBinForPayload,
  normalizeMetadataPayload,
} from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/helpers';
import { IRoutingRulesetForm } from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/types';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType } from 'utils/types';

import {
  normalizeBulkCopyRulesetsResult,
  normalizedRulesetsPaymentMethods,
  normalizeMergeOperation,
  normalizeMergeOperationDetail,
  normalizeOperators,
  normalizeRevertApplyResult,
  normalizeRevertPreviewResult,
  normalizeRoutingRuleset,
  normalizeSingleRoutingRuleset,
} from './normalizers';
import { normalizeRulesetInWorker } from './normalizeRuleset.worker.factory';
import {
  IBulkCopyRulesetsPayload,
  IBulkCopyRulesetsResult,
  ICopyRulesetsPayload,
  IMergeOperationDetail,
  IMergeOperationDetailData,
  IMergeOperationResult,
  IRevertApplyPayload,
  IRevertApplyResult,
  IRevertPreviewPayload,
  IRevertPreviewResult,
  IRoutingRuleset,
  IRoutingRulesetData,
  IRoutingRulesetOperators,
  IRulesetsGroupEdit,
  IRulesetsPaymentMethods,
  ISingleRuleset,
  IUpdateRulesetPayload,
} from './types';

const IRoutingRulesetApi = {
  async getAllRoutingRulesets(
    filters: IAllPageFilters,
  ): Promise<IPageType<IRoutingRuleset>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('routing-rulesets', {
      params: normalizedFilter,
    });

    const searchString = JSON.stringify(filters.search);

    try {
      return await normalizeRulesetInWorker<IPageType<IRoutingRuleset>>(
        response.data,
        searchString,
      );
    } catch {
      return normalizePageData<IRoutingRulesetData, IRoutingRuleset>(
        response.data,
        normalizeRoutingRuleset,
        searchString,
      );
    }
  },
  async getRoutingRuleset(id: string): Promise<ISingleRuleset> {
    const response = await axiosInstance.get(`routing-rulesets/${id}`);

    return normalizeSingleRoutingRuleset(response?.data);
  },

  async groupEdit({ filters, settings }: IRulesetsGroupEdit): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(`routing-rulesets`, normalizedSettings, {
      params: { search },
    });

    return response.data;
  },

  async revertPreview({
    filters,
    includeDeleted,
    timestamp,
  }: IRevertPreviewPayload): Promise<IRevertPreviewResult> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.post(
      `routing-rulesets/revert/preview`,
      { includeDeleted, timestamp },
      {
        params: { search },
      },
    );

    return normalizeRevertPreviewResult(response.data);
  },

  async revertApply({ previewToken }: IRevertApplyPayload): Promise<IRevertApplyResult> {
    const response = await axiosInstance.post(`routing-rulesets/revert/apply`, {
      previewToken,
    });

    return normalizeRevertApplyResult(response.data);
  },

  async mergeEdit({ filters, settings }: IRulesetsGroupEdit): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(
      `routing-rulesets/merge`,
      normalizedSettings,
      {
        params: { search },
      },
    );

    return response.data;
  },

  async createRoutingRuleset(payload: IRoutingRulesetForm): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData({
      ...payload,
      bin: normalizeBinForPayload(payload.bin),
      transaction: {
        ...payload.transaction,
        metadata: normalizeMetadataPayload(payload.transaction.metadata),
      },
    });

    const result = await axiosInstance.post(`routing-rulesets`, normalizedPayload);
    return normalizeBaseGist(result?.data);
  },

  async updateRoutingRuleset({ id, payload }: IUpdateRulesetPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData({
      ...payload,
      transaction: {
        ...payload.transaction,
        metadata: normalizeMetadataPayload(payload.transaction.metadata),
      },
    });

    const result = await axiosInstance.put(`routing-rulesets/${id}`, normalizedPayload);
    return normalizeBaseGist(result?.data);
  },

  async routingRulesetOperators(): Promise<IRoutingRulesetOperators> {
    const result = await axiosInstance.get(`routing-rulesets/operators`);
    return normalizeOperators(result.data);
  },

  async getAllPaymentMethods(
    filters?: IAllPageFilters,
  ): Promise<IRulesetsPaymentMethods> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters || {});
    const response = await axiosInstance.get(`routing-rulesets/payment-methods`, {
      params: normalizedFilter,
    });

    return normalizedRulesetsPaymentMethods(response.data);
  },

  async deleteRoutingRulesets(ids: number[]): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`routing-rulesets`, {
      data: { value: ids },
    });

    return response.data;
  },

  async getRulesetsTerminalLinks(
    filters?: IAllPageFilters,
  ): Promise<Record<string, string[]>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters || {});
    const response = await axiosInstance.get(`routing-rulesets/terminal-links`, {
      params: normalizedFilter,
    });

    return response.data;
  },

  async bulkCopyRulesets(
    payload: IBulkCopyRulesetsPayload,
  ): Promise<IBulkCopyRulesetsResult> {
    const normalizedPayload = prepareFormData(payload);
    const result = await axiosInstance.post(
      `routing-rulesets/bulk-copy`,
      normalizedPayload,
    );
    return normalizeBulkCopyRulesetsResult(result?.data);
  },

  async copyRoutingRuleset(payload: ICopyRulesetsPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);
    const result = await axiosInstance.post(`routing-rulesets/copy`, normalizedPayload);
    return normalizeBaseGist(result?.data);
  },

  async getRulesetsCashierPaymentMethods(filters?: IAllPageFilters): Promise<string[]> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters || {});

    const response = await axiosInstance.get(`routing-rulesets/cashier-payment-methods`, {
      params: { search: normalizedFilter.search },
    });

    return response.data;
  },

  async getTrustedWithoutMinStats(filters: IAllPageFilters): Promise<number[]> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(
      'routing-rulesets/trusted-without-min-stats',
      { params: { search: normalizedFilter.search } },
    );

    return response.data?.result || [];
  },

  async getMergeOperation(operationId: number): Promise<IMergeOperationResult> {
    const response = await axiosInstance.get(
      `routing-rulesets/merge/operations/${operationId}`,
    );
    return normalizeMergeOperation(response.data);
  },

  async getMergeOperationDetails(
    operationId: number,
    filters: IAllPageFilters,
  ): Promise<IPageType<IMergeOperationDetail>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);
    const response = await axiosInstance.get(
      `routing-rulesets/merge/details/${operationId}`,
      { params: normalizedFilter },
    );
    return normalizePageData<IMergeOperationDetailData, IMergeOperationDetail>(
      response.data,
      normalizeMergeOperationDetail,
      JSON.stringify(filters.search),
    );
  },
};

export default IRoutingRulesetApi;
